/**
 * Applies the crud layer's compiled `filter[]` strings (`field||$op||value`) to rows that
 * are already in memory — for lists that come back whole from a source with no filter
 * support: better-auth's members and invitations, and the org branches helper, which sends
 * no query at all. Only the operators the enum / boolean / reference chips compile are
 * understood ($eq, $ne, $in). Anything else is ignored with a dev warning rather than
 * silently matching nothing, which would read as "no such rows".
 */
export function filterInMemory<T>(rows: T[], filters: string[] | undefined): T[] {
  if (!filters?.length) {
    return rows;
  }
  const predicates = filters.flatMap((raw) => {
    const [field, op, value = ''] = raw.split('||');
    // String(): the boolean chip compiles 'true'/'false' while the rows hold real booleans.
    const read = (row: T) => String((row as Record<string, unknown>)[field]);
    switch (op) {
      case '$eq':
        return [(row: T) => read(row) === value];
      case '$ne':
        return [(row: T) => read(row) !== value];
      case '$in': {
        const allowed = value.split(',');
        return [(row: T) => allowed.includes(read(row))];
      }
      default:
        console.warn(`[crud] filterInMemory ignores "${raw}": ${op} is not applied in memory.`);
        return [];
    }
  });
  return rows.filter((row) => predicates.every((matches) => matches(row)));
}
